import { z } from 'zod';
import {
  PracticeProgressSchema,
  AuthorDraftSchema,
  PaginationMetaSchema,
  type PracticeProgress,
  type AuthorDraft,
  type PaginationMeta,
} from '@shared/schemas';
import { apiFetch } from '../client';

/**
 * Practice endpoints · wrap the `practice` edge function (5 endpoints).
 *
 * Per parity-ledger SM.4 / `supabase/functions/practice/index.ts`:
 *   GET /practice/scenarios/:id/progress   → {progress: PracticeProgress}
 *   PUT /practice/scenarios/:id/progress   → {progress: PracticeProgress}
 *   GET /practice/scenarios/:id/draft      → {draft: AuthorDraft}
 *   PUT /practice/scenarios/:id/draft      → {draft: AuthorDraft}
 *   GET /practice/all                      → {data: PracticeProgress[], pagination}
 *
 * `client.ts` strips `/api` and routes to `<supabase>/functions/v1/practice/...`.
 * All endpoints are RLS-scoped (composite PK `user_id, scenario_id`).
 */

const ProgressResponse = z.object({ progress: PracticeProgressSchema });
const DraftResponse = z.object({ draft: AuthorDraftSchema });
const AllResponse = z.object({
  data: z.array(PracticeProgressSchema),
  pagination: PaginationMetaSchema,
});

interface SignalOpts {
  signal?: AbortSignal;
}

export const practiceEndpoints = {
  async getScenarioProgress(
    scenarioId: string,
    opts: SignalOpts = {},
  ): Promise<PracticeProgress> {
    const res = await apiFetch(
      ProgressResponse,
      `/api/practice/scenarios/${encodeURIComponent(scenarioId)}/progress`,
      { signal: opts.signal },
    );
    return res.progress;
  },

  async saveScenarioProgress(
    scenarioId: string,
    body: PracticeProgress,
    opts: SignalOpts = {},
  ): Promise<PracticeProgress> {
    const res = await apiFetch(
      ProgressResponse,
      `/api/practice/scenarios/${encodeURIComponent(scenarioId)}/progress`,
      { method: 'PUT', body, signal: opts.signal },
    );
    return res.progress;
  },

  async getScenarioDraft(scenarioId: string, opts: SignalOpts = {}): Promise<AuthorDraft> {
    const res = await apiFetch(
      DraftResponse,
      `/api/practice/scenarios/${encodeURIComponent(scenarioId)}/draft`,
      { signal: opts.signal },
    );
    return res.draft;
  },

  async saveScenarioDraft(
    scenarioId: string,
    body: AuthorDraft,
    opts: SignalOpts = {},
  ): Promise<AuthorDraft> {
    const res = await apiFetch(
      DraftResponse,
      `/api/practice/scenarios/${encodeURIComponent(scenarioId)}/draft`,
      { method: 'PUT', body, signal: opts.signal },
    );
    return res.draft;
  },

  async getAllProgress(opts: SignalOpts = {}): Promise<{
    data: PracticeProgress[];
    pagination: PaginationMeta;
  }> {
    return apiFetch(AllResponse, '/api/practice/all', { signal: opts.signal });
  },
};
